import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Column } from '~/components';
import { dateStringFormatter } from '~/features/utils';
import { Attestation } from '~/types';

export interface ColumnsProps {
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
}

const TAGS = CT.attestations.table;

export const CreateAttestationColumns = (props: ColumnsProps): Column[] => {
  const { handleEdit, handleDelete } = props;

  const columns: Column[] = [
    {
      label: 'Attestations',
      sortKey: 'type',
      cyTag: CT.attestations.table.type,
      data: (row) => {
        const attestation = row as Attestation;
        return (
          <div className="flex gap-2 items-center">
            <p>
              Attestation --
              {attestation.formInstance.createdAt
                ? new Date(attestation.formInstance.createdAt).toLocaleDateString()
                : 'Not Submitted'}
            </p>
          </div>
        );
      },
    },
    {
      label: 'Status',
      cyTag: CT.attestations.table.status,
      data: (row) => {
        const attestation = row as Attestation;
        return (
          <div className="flex gap-2 items-center">
            {attestation.dateSubmitted ? (
              <CheckCircleIcon
                data-cy={TAGS.check}
                className="size-5 text-success"
                aria-hidden="true"
              />
            ) : (
              <ExclamationCircleIcon
                data-cy={TAGS.warning}
                className="size-5 text-danger"
                aria-hidden="true"
              />
            )}
          </div>
        );
      },
    },
    {
      label: 'Actions',
      cyTag: CT.attestations.table.actions,
      data: (row) => {
        const attestation = row as Attestation;
        return (
          <div className="flex gap-2 items-center">
            {attestation.dateSubmitted ? null : (
              <div className="flex gap-2">
                <PencilIcon
                  className="size-5 text-black-400"
                  aria-hidden="true"
                  data-cy={TAGS.editBtn}
                  onClick={() => handleEdit(attestation.id)}
                />
                <TrashIcon
                  className="size-5 text-danger"
                  aria-hidden="true"
                  data-cy={TAGS.deleteBtn}
                  onClick={() => handleDelete(attestation.id)}
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];
  return columns;
};

export const attestationCols: ColDef<Attestation>[] = [
  { field: 'id', hide: true },
  {
    headerName: 'Attestations',
    field: 'dateSubmitted',
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: ({ data }) => {
      if (!data) return '';
      return data.dateSubmitted
        ? `Attestation -- ${dateStringFormatter(data.dateSubmitted, 'NUMERIC')}`
        : 'Attestation -- Not Submitted';
    },
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const timeA = nodeA?.data?.dateSubmitted ? new Date(nodeA.data.dateSubmitted).getTime() : 0;
      const timeB = nodeB?.data?.dateSubmitted ? new Date(nodeB.data.dateSubmitted).getTime() : 0;
      return timeA - timeB;
    },
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      return (
        <div className="flex gap-2 items-center">
          <p>
            {`Attestation -- ${data.dateSubmitted ? dateStringFormatter(data.dateSubmitted, 'NUMERIC') : 'Not Submitted'}`}
          </p>
        </div>
      );
    },
  },
  {
    headerName: 'Status',
    field: 'dateSubmitted',
    colId: 'status',
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: ({ data }) => (data?.dateSubmitted ? 'Complete' : 'Incomplete'),
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      return (
        <div className="flex gap-2 items-center">
          {data.dateSubmitted ? (
            <CheckCircleIcon
              data-cy={TAGS.check}
              className="size-5 text-success"
              aria-hidden="true"
            />
          ) : (
            <ExclamationCircleIcon
              data-cy={TAGS.warning}
              className="size-5 text-danger"
              aria-hidden="true"
            />
          )}
        </div>
      );
    },
  },
];
